import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  Output,
  signal,
  ViewChild
} from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { EMPTY, finalize, from, map, Observable, switchMap, take, tap } from 'rxjs';
import dayjs from 'dayjs';
import { ApiErrorResponse } from '../../../../core/classes/ApiErrorResponse';
import { ApiErrorCodes, ErrorCodes } from '../../../../core/enums/ErrorCodes';
import { IResponse } from '../../../../core/interfaces/IResponse';
import { BaseModalComponent } from '../../../../core/layout/components/abstracts/base-modal.component';
import { AddItemsToCart, CardActionType } from '../../../../core/types';
import { throwApiError } from '../../../../core/utils/ErrorUtils';
import { ConfirmationModalComponent } from '../../../../shared/components/confirmation-modal/confirmation-modal.component';
import { FormControlComponent } from '../../../../shared/components/form-control/form-control.component';
import { ModalComponent } from '../../../../shared/components/modal/modal.component';
import { TranslatePipe } from '../../../../shared/pipes/trans.pipe';
import { CartItemTypes } from '../../../cart/core/enums/CartItemTypes';
import { ICardPaymentDialogData } from '../../../cart/core/interfaces/ICardPaymentDialogData';
import { ICart } from '../../../cart/core/interfaces/ICart';
import { ICartItem } from '../../../cart/core/interfaces/ICartItem';
import { CardPaymentValidationService } from '../../../cart/core/services/CardPaymentValidationService';
import { CartService } from '../../../cart/core/services/CartService';
import { CartStore } from '../../../cart/core/stores/CartStore';
import { IVoucherDetails } from '../../../vouchers/core/interfaces/IVoucherDetails';
import { IVoucherTransaction } from '../../../vouchers/core/interfaces/IVoucherTransaction';
import { VouchersService } from '../../../vouchers/core/services/VouchersService';
import { CardStatuses } from '../../core/enums/CardStatuses';
import { CardTypeKeys } from '../../core/enums/CardTypeKeys';
import { ICard } from '../../core/interfaces/ICard';
import { ICardProduct } from '../../core/interfaces/ICardProduct';
import { ICardType } from '../../core/interfaces/ICardType';
import { CardsService } from '../../core/services/CardsService';
import { CardsStore } from '../../core/stores/CardsStore';

@Component({
  selector: 'app-card-code-input-modal',
  standalone: true,
  imports: [
    TranslatePipe,
    ModalComponent,
    FormControlComponent,
    FormsModule,
    ReactiveFormsModule,
  ],
  templateUrl: './card-code-input-modal.component.html',
  styleUrl: './card-code-input-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CardCodeInputModalComponent extends BaseModalComponent<ICardType> {

  @ViewChild('codeInput') codeInput: ElementRef<HTMLInputElement>;
  @ViewChild('cardProductsSelectionModal') cardProductsSelectionModal: ModalComponent;

  @Input() addCardToCartApiCall: (cardType: ICardType, additionalData: unknown) => Observable<IResponse<ICard>>;

  /** When true, successful `add-payment-method` skips card-product sub-modal and product fetch; emits `addPaymentMethodSuccess` then closes. */
  @Input() skipCardProductsAfterAttach = false;

  @Input() exchangeWithFee = false;

  @Input() exchangeOldCard: ICard = null;

  @Output() codeSubmitted = new EventEmitter<{ cardType?: ICardType; card?: ICard; cardActionType?: CardActionType; code?: string; }>();
  @Output() cardPaymentDialogRequired = new EventEmitter<ICardPaymentDialogData>();
  @Output() voucherDetailsRequested = new EventEmitter<{ voucherDetails: IVoucherDetails; transactions: IVoucherTransaction[] }>();
  @Output() addPaymentMethodSuccess = new EventEmitter<{ cardNr: string }>();

  public code: string;
  public cardProducts: ICardProduct[] = [];
  public cardActionType: CardActionType;
  public static instance: CardCodeInputModalComponent;
  public readonly isLoading = signal(false);

  private readonly _cardsService = inject(CardsService);
  private readonly _cardsStore = inject(CardsStore);
  private readonly _cartService = inject(CartService);
  private readonly _cartStore = inject(CartStore);
  private readonly _cardPaymentValidationService = inject(CardPaymentValidationService);
  private readonly _vouchersService = inject(VouchersService);
  private readonly _destroyRef = inject(DestroyRef);

  public override open(val?: ICardType, cardActionType: CardActionType = 'add-card', code?: string): void {
    CardCodeInputModalComponent.instance = this;
    this.code = code || null;
    this.isLoading.set(false);
    this.cardActionType = cardActionType;

    if (this.code) {
      this.onSubmit();
    } else {
      super.open(val);
      this.codeInput?.nativeElement?.focus();
    }
  }

  public override close(): void {
    CardCodeInputModalComponent.instance = null;
    super.close();
  }

  public onSubmit(): void {
    this.isLoading.set(true);

    switch (this.cardActionType) {
      case 'add-card':
        this.addCardToCart();
        break;
      case 'return-on-card':
      case 'apply-employee-prices':
        this.codeSubmitted.emit({ code: this.code, cardActionType: this.cardActionType });
        break;
      case 'submit-gildepass-code':
        this.codeSubmitted.emit({ code: this.code, cardActionType: this.cardActionType });
        this.isLoading.set(false);
        this.close();
        break;
      case 'load-card-balance':
        this.loadCardBalance();
        break;
      case 'withdraw-card-balance':
        this.withdrawCardBalance();
        break;
      case 'undo-last-load':
        this.undoLastLoad();
        break;
      case 'cancel-customer-card':
        this.cancelCustomerCard();
        break;
      case 'add-payment-method':
        this.addPaymentMethod();
        break;
      case 'fetch-card-details':
        this.fetchCardDetails();
        break;
      case 'exchange-card-old':
        this.readExchangeOldCard();
        break;
      case 'exchange-card-new':
        this.readExchangeNewCard();
        break;
    }
  }

  public addProductToCart(product: ICardProduct, productEL: HTMLElement): void {
    const itemToAdd: Partial<ICartItem> = {
      itemId: product.productId,
      itemType: CartItemTypes.Product,
      quantity: 1,
      cardId: product.cardId,
      cardProductId: product.id
    };

    productEL.classList.add('disabled');

    this._cartService.addConcessionItemToCart(itemToAdd).subscribe({
      next: () => {
        this.setCardProducts(this.cardProducts);

        if (this.cardProducts.length === 0) {
          this.cardProductsSelectionModal.close();
        }
      },
      error: ({ error }: ApiErrorResponse) => {
        productEL.classList.remove('disabled');
        this.notificationService.pushError(
          'Fehler'.trans('notifications/error.Title'),
          error.message.trans(`add-product-item-to-cart/notifications/${error.message}.Message`)
        );
      }
    });
  }

  private fetchCardDetails(): void {
    this._cardsService.getCardByNumber(this.code)
      .pipe(
        finalize(() => this.isLoading.set(false)),
      )
      .subscribe({
        next: (res) => {
          this.codeSubmitted.emit({ card: res.data, cardActionType: this.cardActionType });
          this.close();
        },
        error: ({ error }: ApiErrorResponse) => {
          if (error.message === ApiErrorCodes.CardNumberBelongsToVoucher) {
            this.openVoucherDetailsConfirmation();
            return;
          }

          this.notificationService.pushError(
            'Fehler'.trans('notifications/error.Title'),
            error.message.trans(`card/notifications/${error.message}.Message`)
          );
        }
      });
  }

  private openVoucherDetailsConfirmation(): void {
    ConfirmationModalComponent.openConfirmationModal(
      'Voucher number'.trans('cards/modals/card-code-input-modal/voucher-found-confirmation.Title'),
      'This number belongs to a voucher. Open voucher details?'.trans('cards/modals/card-code-input-modal/voucher-found-confirmation.Message'),
      this._destroyRef,
      () => this.openVoucherDetails()
    );
  }

  private openVoucherDetails(): void {
    this.isLoading.set(true);

    this._vouchersService.getVoucherByNumber(this.code)
      .pipe(
        switchMap((voucherResponse) => this._vouchersService.getVoucherTransactionsByNumber(this.code)
          .pipe(
            map((transactionsResponse) => [voucherResponse, transactionsResponse] as const)
          )
        ),
        finalize(() => this.isLoading.set(false))
      )
      .subscribe({
        next: ([voucherResponse, transactionsResponse]) => {
          const voucherDetails: IVoucherDetails = {
            voucherId: voucherResponse.data?.voucherId,
            voucherNumber: voucherResponse.data?.voucherNr || this.code,
            balance: voucherResponse.data?.amount || 0,
            status: voucherResponse.data?.voucherStatus || '',
            validUntil: voucherResponse.data?.validUntil || null,
            name: voucherResponse.data?.name || '',
            cancelInfo: voucherResponse.data?.cancelInfo || ''
          };

          this.close();
          this.voucherDetailsRequested.emit({
            voucherDetails,
            transactions: transactionsResponse.data || []
          });
        },
        error: ({ error }: ApiErrorResponse) => {
          this.notificationService.pushError(
            'Fehler'.trans('notifications/error.Title'),
            error.message.trans(`get-voucher-details/notifications/${error.message}.Message`)
          );
        }
      });
  }

  private readExchangeOldCard(): void {
    this._cardsService.getCardByNumber(this.code)
      .pipe(
        finalize(() => this.isLoading.set(false)),
        map((res: IResponse<ICard>) => ({
          card: res.data,
          cardType: this._cardsStore.allCardTypes().find(x => x.cardTypeId === res.data.cardTypeId)
        })),
        tap(res => this.validateExchangeOldCard(res.card, res.cardType))
      )
      .subscribe({
        next: (res) => {
          this.codeSubmitted.emit({
            card: res.card,
            cardActionType: this.cardActionType
          });
        },
        error: ({ error }: ApiErrorResponse) => {
          this.notificationService.pushError(
            'Fehler'.trans('notifications/error.Title'),
            error.message.trans(`card/notifications/${error.message}.Message`)
          );
        }
      });
  }

  private readExchangeNewCard(): void {
    this._cardsService.getCardByNumber(this.code)
      .pipe(
        finalize(() => this.isLoading.set(false)),
        map((res: IResponse<ICard>) => ({
          card: res.data,
          cardType: this._cardsStore.allCardTypes().find(x => x.cardTypeId === res.data.cardTypeId)
        })),
        tap(res => this.validateExchangeNewCard(res.card, res.cardType))
      )
      .subscribe({
        next: (res) => {
          this.codeSubmitted.emit({
            card: res.card,
            cardActionType: this.cardActionType
          });
        },
        error: ({ error }: ApiErrorResponse) => {
          this.notificationService.pushError(
            'Fehler'.trans('notifications/error.Title'),
            error.message.trans(`card/notifications/${error.message}.Message`)
          );
        }
      });
  }

  private openAddVoucherInsteadConfirmation(): void {
    ConfirmationModalComponent.openConfirmationModal(
      'Voucher number'.trans('cards/modals/card-code-input-modal/add-voucher-confirmation.Title'),
      'This number belongs to a voucher. Add voucher instead?'.trans('cards/modals/card-code-input-modal/add-voucher-confirmation.Message'),
      this._destroyRef,
      () => this.addVoucherInstead()
    );
  }

  private addVoucherInstead(): void {
    this.isLoading.set(true);

    this._vouchersService.getVoucherByNumber(this.code)
      .pipe(
        switchMap((voucherResponse) => {
          const validUntil = voucherResponse.data?.validUntil;

          if (validUntil && dayjs(validUntil).isBefore(dayjs())) {
            const formattedDate = dayjs(validUntil).format('DD-MM-YYYY');
            const confirmationText = `This voucher is expired since: <b>[[formattedDate]]</b><br>Do you still want to redeem it?`.trans('vouchers/modals/voucher-code-input-modal/voucher-expired-confirmation.Message', [['formattedDate', formattedDate]]);

            return from(
              new Promise<boolean>((resolve) => {
                let resolved = false;

                ConfirmationModalComponent.openConfirmationModal(
                  'Voucher Expired'.trans('vouchers/modals/voucher-code-input-modal/voucher-expired-confirmation.Title'),
                  confirmationText,
                  this._destroyRef,
                  () => {
                    if (!resolved) {
                      resolved = true;
                      resolve(true);
                    }
                  }
                );

                ConfirmationModalComponent.instance.onCancel
                  .pipe(take(1))
                  .subscribe(() => {
                    if (!resolved) {
                      resolved = true;
                      resolve(false);
                    }
                  });
              })
            ).pipe(
              switchMap((confirmed) => confirmed ? this.addVoucherInsteadToCart() : EMPTY)
            );
          }

          return this.addVoucherInsteadToCart();
        }),
        finalize(() => this.isLoading.set(false))
      )
      .subscribe({
        next: (res) => {
          if (res && !(res.message in ApiErrorCodes)) {
            this.close();
          }
        },
        error: ({ error }: ApiErrorResponse) => {
          this.notificationService.pushError(
            'Fehler'.trans('notifications/error.Title'),
            error.message.trans(`cash-voucher-in-cart/notifications/${error.message}.Message`)
          );
        }
      });
  }

  private addVoucherInsteadToCart(): Observable<IResponse<ICart>> {
    const items: AddItemsToCart = [
      {
        itemId: 0,
        itemType: CartItemTypes.CashVoucher,
        voucherNumber: this.code,
        quantity: 1
      }
    ];

    return this._cartService.addItemToCart(items);
  }

  private validateExchangeOldCard(card: ICard, cardType: ICardType): void {
    if (card.cardTypeKey === CardTypeKeys.CRD_EMPLOYEE) {
      throwApiError(ApiErrorCodes.CardCannotBeExchanged);
    }

    if (!cardType) {
      throwApiError(ApiErrorCodes.CardTypeNotFound);
    }

    if (!cardType.flagCashCard) {
      throwApiError(ApiErrorCodes.CardNotCashCard);
    }

    if (this.exchangeWithFee) {
      if (
        card.status === CardStatuses.CRD_ACTIVATED
        || card.status === CardStatuses.CRD_DEACTIVATED
      ) {
        throwApiError(ApiErrorCodes.CardCannotBeExchanged);
      }
    } else if (card.status === CardStatuses.CRD_ACTIVATED) {
      throwApiError(ApiErrorCodes.CardCannotBeExchanged);
    } else if (
      card.status === CardStatuses.CRD_DEACTIVATED
      && !this.permissionService.hasPermission(this.permissionKeys.WebCxCashCardExchangeWithoutFee)
    ) {
      throwApiError(ApiErrorCodes.CardExchangeWithoutFeeNotAllowed);
    }

    if ((card.currentBalance ?? 0) <= 0) {
      throwApiError(ApiErrorCodes.InvalidCardAmount);
    }
  }

  private validateExchangeNewCard(card: ICard, cardType: ICardType): void {
    if (!this.exchangeOldCard) {
      throwApiError(ApiErrorCodes.CardNotFound);
    }

    if (card.cardNr === this.exchangeOldCard.cardNr) {
      throwApiError(ApiErrorCodes.ExchangeCardsMustDiffer);
    }

    if (!cardType) {
      throwApiError(ApiErrorCodes.CardTypeNotFound);
    }

    if (card.status !== CardStatuses.CRD_ACTIVATED) {
      throwApiError(ApiErrorCodes.CardNotActivated);
    }
  }

  private addCardToCart(): void {
    if (this.data?.cardTypeId) {
      this.addCardToCartApiCall(this.data, { cardNumber: this.code })
        .pipe(
          finalize(() => this.isLoading.set(false))
        )
        .subscribe(
          (res: IResponse<ICard>) => {
            if (!res) {
              return;
            }

            if (res.message === ApiErrorCodes.CardNumberBelongsToVoucher) {
              this.openAddVoucherInsteadConfirmation();
              return;
            }

            if (this.data.minLoadAmount !== this.data.maxLoadAmount || this.data.minLoadAmount === 0) {
              this.codeSubmitted.emit({
                cardType: this.data,
                card: res.data,
                cardActionType: this.cardActionType
              });
            }

            this.close();
          }
        );
    }
  }

  private loadCardBalance(): void {
    this._cardsService.getCardByNumber(this.code)
      .pipe(
        finalize(() => this.isLoading.set(false)),
        map((res: IResponse<ICard>) => {
          return {
            card: res.data,
            cardType: this._cardsStore.allCardTypes().find(x => x.cardTypeId === res.data.cardTypeId)
          };
        }),
        tap(res => {
          if (res.card.status !== CardStatuses.CRD_VALID) {
            throwApiError(ApiErrorCodes.CardNotValid);
          }

          if (!res.cardType) {
            throwApiError(ApiErrorCodes.CardTypeNotFound);
          }

          if (res.cardType.flagNotLoadable) {
            throwApiError(ApiErrorCodes.CardNotLoadable);
          }

          if (!res.cardType.flagCashCard) {
            throwApiError(ApiErrorCodes.CardNotCashCard);
          }

          if (res.cardType.maximumAmount > 0) {
            const remainingToCap = res.cardType.maximumAmount - (res.card.currentBalance || 0);

            if (remainingToCap <= 0) {
              throwApiError(ErrorCodes.CardMaximumAmountReached);
            }

            if ((res.cardType.minLoadAmount || 0) > 0 && remainingToCap < res.cardType.minLoadAmount) {
              throwApiError(ErrorCodes.CardMinimumAmountNotReached);
            }
          }
        })
      )
      .subscribe({
        next: (res) => {
          this.codeSubmitted.emit({
            cardType: res.cardType,
            card: res.card,
            cardActionType: this.cardActionType
          });
          this.close();
        },
        error: ({ error }: ApiErrorResponse) => {
          this.notificationService.pushError(
            'Fehler'.trans('notifications/error.Title'),
            error.message.trans(`card/notifications/${error.message}.Message`)
          );
        }
      });
  }

  private withdrawCardBalance(): void {
    this._cardsService.getCardByNumber(this.code)
      .pipe(
        finalize(() => this.isLoading.set(false)),
        map((res: IResponse<ICard>) => {
          return {
            card: res.data,
            cardType: this._cardsStore.allCardTypes().find(x => x.cardTypeId === res.data.cardTypeId)
          };
        }),
        tap(res => {
          if (res.card.status !== CardStatuses.CRD_VALID) {
            throwApiError(ApiErrorCodes.CardNotValid);
          }

          if (!res.cardType) {
            throwApiError(ApiErrorCodes.CardTypeNotFound);
          }

          if (!res.cardType.flagCashCard) {
            throwApiError(ApiErrorCodes.CardNotCashCard);
          }

          if (res.card.cardTypeKey === CardTypeKeys.CRD_EMPLOYEE) {
            throwApiError(ApiErrorCodes.InvalidCardType);
          }

          if ((res.card.currentBalance ?? 0) <= 0) {
            throwApiError(ApiErrorCodes.InvalidCardAmount);
          }
        })
      )
      .subscribe({
        next: (res) => {
          this.codeSubmitted.emit({
            cardType: res.cardType,
            card: res.card,
            cardActionType: this.cardActionType
          });
          this.close();
        },
        error: ({ error }: ApiErrorResponse) => {
          this.notificationService.pushError(
            'Fehler'.trans('notifications/error.Title'),
            error.message.trans(`card/notifications/${error.message}.Message`)
          );
        }
      });
  }

  private undoLastLoad(): void {
    this._cartService.undoLastCardLoad(this.code)
      .pipe(
        finalize(() => this.isLoading.set(false)),
      )
      .subscribe({
        next: () => this.close(),
        error: ({ error }: ApiErrorResponse) => {
          this.notificationService.pushError(
            'Fehler'.trans('notifications/error.Title'),
            error.message.trans(`card/notifications/${error.message}.Message`)
          );
        }
      });
  }

  private cancelCustomerCard(): void {
    this._cartService.cancelCustomerCard(this.code)
      .pipe(
        finalize(() => this.isLoading.set(false)),
      )
      .subscribe({
        next: () => this.close(),
        error: ({ error }: ApiErrorResponse) => {
          this.notificationService.pushError(
            'Fehler'.trans('notifications/error.Title'),
            error.message.trans(`card/notifications/${error.message}.Message`)
          );
        }
      });
  }

  private addPaymentMethod(): void {
    this.cardProducts = [];

    this._cartService.attachCardToCart(this.code, { suppressedMessages: [ApiErrorCodes.CardNumberBelongsToVoucher] }).subscribe({
      next: (res) => {
        if (res.message === ApiErrorCodes.CardNumberBelongsToVoucher) {
          this.isLoading.set(false);
          this.openAddVoucherInsteadConfirmation();
          return;
        }

        const card = this._cartStore.cart().customerCards.find(c => c.cardNr === this.code);

        if (this.skipCardProductsAfterAttach) {
          this.addPaymentMethodSuccess.emit({ cardNr: this.code });
          this.isLoading.set(false);
          this.close();
          return;
        }

        if (card) {
          const cardType = this._cardsStore.allCardTypes().find(ct => ct.cardTypeId === card.cardTypeId);
          const cardIsAuthorizeOnly = cardType && this._cardsStore.isAuthorizeOnly(cardType);

          if (!cardIsAuthorizeOnly) {
            const validationResult = this._cardPaymentValidationService.validateCardPayment(card.cardId);

            if (validationResult.shouldShowDialog && validationResult.dialogData) {
              this.cardPaymentDialogRequired.emit(validationResult.dialogData);
            }
          }

          this._cardsService.getCardProducts(card.cardId)
            .pipe(
              finalize(() => this.isLoading.set(false)),
            )
            .subscribe({
              next: res => {
                this.setCardProducts(res.data);

                if (this.cardProducts.length > 0) {
                  this.cardProductsSelectionModal.open();
                }

                this.close();
              },
              error: ({ error }: ApiErrorResponse) => {
                this.notificationService.pushError(
                  'Fehler'.trans('notifications/error.Title'),
                  error.message.trans(`card-products/notifications/${error.message}.Message`)
                );
              }
            });
        } else {
          this.isLoading.set(false);
        }
      },
      error: ({ error }: ApiErrorResponse) => {
        this.isLoading.set(false);

        if (error.message === ApiErrorCodes.CardNumberBelongsToVoucher) {
          this.openAddVoucherInsteadConfirmation();
          return;
        }

        this.notificationService.pushError(
          'Fehler'.trans('notifications/error.Title'),
          error.message.trans(`attach-card/notifications/${error.message}.Message`)
        );
      }
    });
  }

  private setCardProducts(cardProducts: ICardProduct[]): void {
    this.cardProducts = cardProducts
      .filter(p => !this._cartStore.cart().items.find(i => i.itemId === p.productId));

    this.cdr.detectChanges();
  }
}
